<script setup lang="ts">
import { pb } from "@/utils/PocketBaseAdapter";
import { requiredValidator } from "@validators";

// Interface
interface Emit {
  (e: "update:isDrawerOpen", value: boolean): void;
  (e: "closeDialog", value?: boolean): void;
  (e: "setAuthUser", value?: any): void;
}

interface Props {
  isDrawerOpen: boolean;
  authUser: any;
}

interface AuthUser {
  firstName: string;
  lastName: string;
  username?: string;
  homeLocation: string | undefined;
}

// Props & Emits
const props = defineProps<Props>();
const emit = defineEmits<Emit>();

// Reactive Data
const refVForm = ref<any>(null);
const locationList = ref<any[]>([]);
const authUser = ref<AuthUser>({
  firstName: "",
  lastName: "",
  username: "",
  homeLocation: undefined,
});

// Methods
/**
 * Retrieves the list of locations from the PocketBase collection "locations".
 * If the list is already populated, it will not make another request.
 *
 * @returns {Promise<void>}
 */
const getLocationList = async () => {
  try {
    // Check if the location list is already populated
    if (locationList.value.length) {
      return;
    }

    // Fetch the list of locations from PocketBase
    const records = await pb.collection("locations").getFullList();

    // If records are returned, update the location list
    if (records) {
      locationList.value = records;
    }
  } catch (e) {
    // Log any errors that occur during the request
    console.log("error getting locations list: ", e);
  }
};

/**
 * Handles the submission of OAuth sign-up data.
 *
 * @function setAuthuserData
 * @returns {Promise<void>}
 */
const setAuthuserData = async () => {
  try {
    // Prepare the user data object
    const authData = {
      username: authUser.value.username,
      firstName: authUser.value.firstName,
      lastName: authUser.value.lastName,
      status: "New",
      homeLocation: authUser.value.homeLocation,
    };

    // Validate the form
    const { valid } = await refVForm.value.validate();

    // If the form is valid, emit the "setAuthUser" event with the prepared data
    if (valid) {
      emit("setAuthUser", authData);
    }
  } catch (error) {
    // Log any errors that occur during the data submission process
    console.log("Error sending OAuthSignUp data: ", error);
  }
};

watch(
  () => props.isDrawerOpen,
  (newVal) => {
    if (newVal) {
      getLocationList();
    }
  }
);

onMounted(() => {
  if (props.authUser && props.isDrawerOpen) {
    authUser.value.username = props.authUser?.username;
    authUser.value.firstName = props.authUser?.firstName || "";
    authUser.value.lastName = props.authUser?.lastName || "";
  }
});
</script>

<template>
  <div>
    <VDialog
      :model-value="props.isDrawerOpen"
      persistent
      max-width="800"
      no-click-animation
      @update:model-value="emit('update:isDrawerOpen', false)"
    >
      <VCard>
        <div
          class="d-flex align-center justify-space-between bg-dialogHeaderColor"
        >
          <VCardTitle>Sign Up</VCardTitle>
          <VBtn
            icon="mdi-close"
            size="large"
            variant="text"
            class="rounded-0"
            color=""
            @click="emit('closeDialog')"
          />
        </div>
        <VCardText>
          <VForm ref="refVForm">
            <VRow>
              <!-- user name  -->
              <VCol cols="12" md="6">
                <VTextField
                  v-model="authUser.username"
                  :rules="[requiredValidator]"
                  label="User Name"
                />
              </VCol>
              <!-- First Name  -->
              <VCol cols="12" md="6">
                <VTextField
                  v-model="authUser.firstName"
                  :rules="[requiredValidator]"
                  label="First Name"
                />
              </VCol>
              <!-- Last Name  -->
              <VCol cols="12" md="6">
                <VTextField
                  v-model="authUser.lastName"
                  :rules="[requiredValidator]"
                  label="Last Name"
                />
              </VCol>
              <!-- Home Location  -->
              <VCol cols="12" md="6">
                <VSelect
                  v-model="authUser.homeLocation"
                  :items="locationList"
                  :rules="[requiredValidator]"
                  item-title="name"
                  item-value="id"
                  label="Home location"
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
        <!-- Dialog Actions  -->
        <VCardActions>
          <VSpacer />
          <VBtn color="success" @click="setAuthuserData"> Save </VBtn>
          <VBtn color="error" @click="emit('closeDialog')"> Close </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped></style>
